/**
 * Утилиты для работы с карьерными переходами
 */

import { 
  TransitionType, 
  normalizeTransitionType, 
  getTransitionTypeLabel,
  type CareerTransition 
} from '../models/TransitionType'

// Интерфейс для сгруппированных переходов
export interface GroupedTransitions {
  vertical: CareerTransition[]
  horizontal: CareerTransition[]
  crossFunctional: CareerTransition[]
  unknown: CareerTransition[]
}

// Интерфейс для счётчиков переходов
export interface TransitionCounts {
  total: number
  vertical: number
  horizontal: number
  crossFunctional: number
  unknown: number
}

/**
 * Группирует переходы по типам
 * @param transitions - массив переходов
 * @returns объект с переходами, сгруппированными по типам
 */
export const groupTransitionsByType = (transitions: CareerTransition[]): GroupedTransitions => {
  const grouped: GroupedTransitions = {
    vertical: [],
    horizontal: [],
    crossFunctional: [],
    unknown: []
  }

  if (!Array.isArray(transitions)) {
    console.warn('groupTransitionsByType: переходы не являются массивом', transitions)
    return grouped
  }

  transitions.forEach((transition) => {
    if (!transition) {
      console.warn('groupTransitionsByType: найден null/undefined переход', transition)
      return
    }

    try {
      const normalizedType = normalizeTransitionType(transition.transition_type)
      
      switch (normalizedType) {
        case TransitionType.VERTICAL:
          grouped.vertical.push(transition)
          break
        case TransitionType.HORIZONTAL:
          grouped.horizontal.push(transition)
          break
        case TransitionType.CROSS_FUNCTIONAL:
          grouped.crossFunctional.push(transition)
          break
        default:
          console.warn(`Неизвестный тип перехода: ${transition.transition_type}`, transition)
          grouped.unknown.push(transition)
      }
    } catch (error) {
      console.error('Ошибка при группировке перехода:', error, transition)
      grouped.unknown.push(transition)
    }
  })

  return grouped
}

/**
 * Подсчитывает количество переходов по типам
 * @param transitions - массив переходов
 * @returns объект со счётчиками переходов по типам
 */
export const countTransitionsByType = (transitions: CareerTransition[]): TransitionCounts => {
  const grouped = groupTransitionsByType(transitions)
  
  return {
    total: transitions.length,
    vertical: grouped.vertical.length,
    horizontal: grouped.horizontal.length,
    crossFunctional: grouped.crossFunctional.length,
    unknown: grouped.unknown.length
  }
}

/**
 * Создает объект статистики переходов для виджета
 * @param transitions - массив переходов
 * @returns объект со статистикой для отображения
 */
export const createTransitionStats = (transitions: CareerTransition[]) => {
  const counts = countTransitionsByType(transitions)
  
  return {
    total: {
      count: counts.total,
      label: 'Всего переходов',
      icon: 'GitBranch',
      color: 'blue'
    },
    vertical: {
      count: counts.vertical,
      label: getTransitionTypeLabel(TransitionType.VERTICAL),
      icon: 'TrendingUp',
      color: 'green'
    },
    horizontal: {
      count: counts.horizontal,
      label: getTransitionTypeLabel(TransitionType.HORIZONTAL),
      icon: 'ArrowRight',
      color: 'blue'
    },
    crossFunctional: {
      count: counts.crossFunctional,
      label: getTransitionTypeLabel(TransitionType.CROSS_FUNCTIONAL),
      icon: 'ArrowUpRight',
      color: 'orange'
    }
  }
}

/**
 * Фильтрует переходы по типу
 * @param transitions - массив переходов
 * @param typeFilter - фильтр по типу ('all' или конкретный тип)
 * @returns отфильтрованный массив переходов
 */
export const filterTransitionsByType = (
  transitions: CareerTransition[], 
  typeFilter: string
): CareerTransition[] => {
  if (typeFilter === 'all') {
    return transitions
  }

  return transitions.filter(transition => {
    const normalizedType = normalizeTransitionType(transition.transition_type)
    const normalizedFilter = normalizeTransitionType(typeFilter)
    return normalizedType === normalizedFilter
  })
}

/**
 * Валидирует данные перехода
 * @param transition - объект перехода
 * @returns объект с результатом валидации
 */
export const validateTransition = (transition: Partial<CareerTransition>) => {
  const errors: string[] = []

  if (!transition.from_position_id) {
    errors.push('Не указана исходная должность')
  }

  if (!transition.to_position_id) {
    errors.push('Не указана целевая должность')
  }

  if (transition.from_position_id === transition.to_position_id) {
    errors.push('Исходная и целевая должности не могут быть одинаковыми')
  }

  if (transition.probability !== undefined && 
      (transition.probability < 0 || transition.probability > 100)) {
    errors.push('Вероятность должна быть от 0 до 100')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Создаёт текстовое описание перехода
 * @param transition - объект перехода
 * @returns строка с описанием перехода
 */
export const formatTransitionDescription = (transition: CareerTransition): string => {
  const typeLabel = getTransitionTypeLabel(transition.transition_type)
  const from = transition.from_position_title || 'Неизвестная должность'
  const to = transition.to_position_title || 'Неизвестная должность'
  
  return `${typeLabel}: ${from} → ${to}`
} 